import { useEffect } from "react";
import { useQueries, useQueryClient } from "react-query";

import { upgradePricePreviewQueries } from "@/react/lib/queries/upgradePricePreview";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

interface UseUpgradePricePreviewsProps {
  checkoutSessionId: number;
  communityId: number;
  couponMode: UpgradePreview["coupon_mode"];
  disabled: boolean;
  paywallId: number;
  preview?: UpgradePreview;
  stepId: number;
  visiblePriceIds: number[];
}

/** Display-only previews must never replace the selected order's acceptance terms. */
export const useUpgradePricePreviews = ({
  communityId,
  checkoutSessionId,
  paywallId,
  stepId,
  visiblePriceIds,
  couponMode,
  preview,
  disabled,
}: UseUpgradePricePreviewsProps) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!preview || preview.coupon_mode !== couponMode) return;
    const option = preview.price_options.find(
      option => option.paywall_price_id === preview.selected_paywall_price_id,
    );
    if (!option) return;
    queryClient.setQueryData(
      upgradePricePreviewQueries.detail({
        communityId,
        checkoutSessionId,
        paywallId,
        stepId,
        priceId: preview.selected_paywall_price_id,
        couponMode,
      }).queryKey,
      option,
    );
  }, [
    checkoutSessionId,
    communityId,
    couponMode,
    paywallId,
    preview,
    queryClient,
    stepId,
  ]);
  const queries = useQueries({
    queries: visiblePriceIds.map(priceId => ({
      ...upgradePricePreviewQueries.detail({
        communityId,
        checkoutSessionId,
        paywallId,
        stepId,
        priceId,
        couponMode,
      }),
      enabled:
        !disabled &&
        visiblePriceIds.length > 1 &&
        Boolean(preview) &&
        priceId !== preview?.selected_paywall_price_id,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  });

  const isLoadingPricePreviews =
    !disabled &&
    Boolean(preview) &&
    visiblePriceIds.length > 1 &&
    queries.some(
      (query, index) =>
        visiblePriceIds[index] !== preview?.selected_paywall_price_id &&
        (query.isFetching || (!query.isSuccess && !query.isError)),
    );

  return {
    isLoadingPricePreviews,
    pricePreviews: queries.flatMap(query =>
      query.isSuccess && query.data ? [query.data] : [],
    ),
  };
};
